import { useMemo, useRef, useState, useEffect } from "react";
import type { KeyboardEvent } from "react"
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import PageTitle from "../../components/ui/PageTitle";
import { Headphones, MagnifyingGlass, X } from "@phosphor-icons/react";
import duasJson from "../../data/duasAudio.json";

type Dua = {
  id: string;
  title: string;
  reciters: { id: string; name: string; url: string; avatar?: string }[];
};

export default function DuaListPage() {
  const all = useMemo(() => duasJson as Dua[], []);
  return (
    <GradientBg>
      <Container>
        <TopBar>
          <PageTitle
            icon={<Headphones size={22} weight="duotone" style={{ marginInlineEnd: 8 }} color="var(--primary)" />}
            center gap={8} as="h1"
          >
            الاستماع للأدعية
          </PageTitle>
        </TopBar>

        <Subtitle>اختر الدعاء للاستماع</Subtitle>
        <DuaSearch duas={all} maxSuggestions={7} />

        <List>
          {all.map((d, i) => (
            <Row key={d.id} to={`/duas/${d.id}`}>
              <Badge>{i + 1}</Badge>
              <Name>{d.title}</Name>
            </Row>
          ))}
        </List>
      </Container>
    </GradientBg>
  );
}

/* ---------- DuaSearch ---------- */
function DuaSearch({ duas, maxSuggestions = 7 }: { duas: Dua[]; maxSuggestions?: number }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey as any);
    return () => window.removeEventListener("keydown", onKey as any);
  }, []);

  const normalized = (s: string) =>
    s.normalize("NFKD")
      .replace(/[\u064B-\u065F\u0610-\u061A\u06D6-\u06ED]/g, "")
      .replace(/[^\p{L}\p{N}\s]/gu, "")
      .trim();

  const results = useMemo(() => {
    const needle = normalized(q);
    if (!needle) return [];
    return duas
      .filter((d) =>
        normalized(d.title).includes(needle) ||
        d.reciters?.some((r) => normalized(r.name).includes(needle))
      )
      .slice(0, maxSuggestions);
  }, [q, duas, maxSuggestions]);

  const go = (id: string) => {
    navigate(`/duas/${id}`);
    setOpen(false);
  };

  return (
    <SearchWrap dir="rtl">
      <SearchBox role="combobox" aria-expanded={open} aria-owns="dua-suggest-list">
        <IconLeft><MagnifyingGlass size={18} /></IconLeft>

        <SearchInput
          ref={inputRef}
          placeholder="ابحث باسم الدعاء أو اسم القارئ… / للتركيز"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => q && setOpen(true)}
          onKeyDown={(e) => {
            if (!open && results.length) setOpen(true);
            if (e.key === "ArrowDown" && results.length) { e.preventDefault(); setActiveIndex((i) => (i + 1) % results.length); }
            else if (e.key === "ArrowUp" && results.length) { e.preventDefault(); setActiveIndex((i) => (i - 1 + results.length) % results.length); }
            else if (e.key === "Enter") { e.preventDefault(); if (results.length) go(results[activeIndex].id); }
            else if (e.key === "Escape") { setOpen(false); (e.target as HTMLInputElement).blur(); }
          }}
          aria-autocomplete="list"
          aria-controls="dua-suggest-list"
        />

        {!!q && (
          <ClearBtn aria-label="مسح" onClick={() => { setQ(""); setOpen(false); inputRef.current?.focus(); }}>
            <X size={16} />
          </ClearBtn>
        )}
      </SearchBox>

      {open && results.length > 0 && (
        <SuggestList id="dua-suggest-list" role="listbox">
          {results.map((d, i) => (
            <SuggestItem key={d.id} role="option" aria-selected={i === activeIndex}
              $active={i === activeIndex} onMouseEnter={() => setActiveIndex(i)} onClick={() => go(d.id)}>
              <SuggestBadge><Headphones size={14} weight="bold" /></SuggestBadge>
              <span>{d.title}</span>
            </SuggestItem>
          ))}
        </SuggestList>
      )}

      {open && q && results.length === 0 && <EmptySuggest>لا توجد نتائج مطابقة</EmptySuggest>}
    </SearchWrap>
  );
}

/* --- styles --- */
const GradientBg = styled.div` min-height:100dvh; display:flex; flex-direction:column; `;
const Container = styled.div` padding:16px clamp(16px,4vw,32px) 28px; flex:1; `;
const TopBar = styled.div` position:relative; display:flex; align-items:center; justify-content:center; min-height:40px; margin-bottom:8px; width:100%; `;
const Subtitle = styled.p` text-align:center; margin:6px 0 16px; color:var(--muted); font-size:.95rem; `;
const List = styled.div` display:flex; flex-direction:column; gap:10px; `;
const Row = styled(Link)` display:flex; align-items:center; gap:12px; padding:14px 12px; border-radius:14px; background:var(--surface); border:1px solid var(--border); box-shadow:var(--shadow-sm); text-decoration:none; transition: transform .12s ease, box-shadow .12s ease; &:hover{ transform:translateY(-2px); box-shadow:0 12px 20px rgba(0,0,0,.12);} `;
const Badge = styled.span` min-width:32px; height:32px; border-radius:16px; background:var(--primary); color:#fff; display:inline-flex; align-items:center; justify-content:center; font-weight:700; font-size:.95rem; `;
const Name = styled.span` flex:1; font-size:2rem; font-family:"Lateef"; color:var(--text); line-height:1; `;

/* search */
const SearchWrap = styled.div` max-width:720px; margin:0 auto 16px; position:relative; `;
const SearchBox = styled.div` position:relative; display:flex; align-items:center; background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:10px 40px; box-shadow:var(--shadow-xs,0 1px 3px rgba(0,0,0,.06)); `;
const IconLeft = styled.div` position:absolute; inset-inline-start:10px; opacity:.7; display:grid; place-items:center; `;
const ClearBtn = styled.button` position:absolute; inset-inline-end:10px; display:grid; place-items:center; width:28px; height:28px; border:0; background:transparent; color:var(--muted); cursor:pointer; `;
const SearchInput = styled.input` width:100%; font-size:.98rem; background:transparent; color:var(--text); border:none; outline:none; &::placeholder{ color:var(--muted); } `;
const SuggestList = styled.ul` position:absolute; inset-inline:0; top:calc(100% + 8px); background:var(--surface); border:1px solid var(--border); border-radius:12px; box-shadow:var(--shadow,0 10px 24px rgba(0,0,0,.12)); padding:6px; margin:0; list-style:none; z-index:10; max-height:360px; overflow:auto; `;
const SuggestItem = styled.li<{ $active?: boolean }>` display:flex; align-items:center; gap:10px; padding:10px 12px; border-radius:10px; cursor:pointer; background:${p=>p.$active?"var(--surface-2, rgba(0,0,0,.05))":"transparent"}; color:var(--text); &:hover{ background:var(--surface-2, rgba(0,0,0,.05)); } `;
const SuggestBadge = styled.span` background:var(--primary); color:#fff; font-weight:600; font-size:.85rem; padding:2px 8px; border-radius:12px; display:inline-flex; align-items:center; gap:6px; `;
const EmptySuggest = styled.div` position:absolute; inset-inline:0; top:calc(100% + 8px); background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:12px; text-align:center; color:var(--muted); z-index:20; box-shadow:var(--shadow,0 10px 24px rgba(0,0,0,.12)); `;
